/**
 * In-flight request coalescing + short-lived result cache.
 *
 * Why this exists: several components independently fetch the same endpoints on
 * mount. Measured on the running app, a single page load hit
 * `/billing/subscription` six times and `/auth/profile` four times — the
 * subscription banner, the trial badge, the user hook, the suspension banner
 * and assorted pages each owned their own `useEffect` with no sharing. On a
 * 1000-requests/hour organization budget that waste is significant.
 *
 * This is deliberately small. The app already has @tanstack/react-query
 * installed but only five call sites use it, so migrating every fetch is a much
 * larger change than the problem requires. What the duplicated calls actually
 * need is:
 *
 *   - concurrent callers share one in-flight promise (no duplicate requests)
 *   - a short freshness window so remounts and sibling components reuse a result
 *
 * Cache entries are keyed by a caller-supplied string and dropped on failure so
 * an error is never served from cache.
 */

/** Freshness window: a result younger than this is reused without refetching. */
export const DEFAULT_TTL_MS = 30_000;

interface CacheEntry<T> {
  value: T;
  storedAt: number;
}

const cache = new Map<string, CacheEntry<unknown>>();
const inFlight = new Map<string, Promise<unknown>>();

export interface SharedResourceOptions {
  /** How long a result stays fresh. Defaults to 30s. */
  ttlMs?: number;
  /** Bypass the cache and force a network request. */
  force?: boolean;
}

/**
 * Resolve a shared resource, coalescing concurrent callers.
 *
 * Returns the cached value when it is still fresh, otherwise joins the in-flight
 * request if one exists, otherwise starts a new one.
 */
export async function fetchShared<T>(
  key: string,
  loader: () => Promise<T>,
  { ttlMs = DEFAULT_TTL_MS, force = false }: SharedResourceOptions = {}
): Promise<T> {
  if (!force) {
    const cached = cache.get(key) as CacheEntry<T> | undefined;
    if (cached && Date.now() - cached.storedAt < ttlMs) {
      return cached.value;
    }
    const pending = inFlight.get(key) as Promise<T> | undefined;
    if (pending) return pending;
  }

  const request = loader()
    .then((value) => {
      cache.set(key, { value, storedAt: Date.now() });
      return value;
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, request);
  return request;
}

/**
 * Drop a cached resource so the next read refetches.
 *
 * Call after a mutation that changes the resource — e.g. a profile update or a
 * plan change — so the UI does not show stale data for the rest of the TTL.
 */
export function invalidateShared(key: string): void {
  cache.delete(key);
  inFlight.delete(key);
}

/** Drop everything. Intended for sign-out and tests. */
export function invalidateAllShared(): void {
  cache.clear();
  inFlight.clear();
}

/** Cache keys, centralised so producers and invalidators cannot drift apart. */
export const SHARED_KEYS = {
  subscription: 'billing:subscription',
  profile: 'auth:profile',
} as const;

export type SharedKey = (typeof SHARED_KEYS)[keyof typeof SHARED_KEYS];
